/**
 * Void gradient — dark purple fill below edge with scanline pulse during rise.
 */

import type { Graphics } from 'pixi.js';
import { WORLD, VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX, lerp, getVoidTheme } from './types';

export function drawGradient(gfx: Graphics, voidY: number, state: VoidState): void {
  gfx.clear();

  const v = visibleX();
  const stripHeight = 4;
  const topFade = 50; // px above voidY where gradient starts
  const startY = voidY - topFade;
  const endY = WORLD.VIRTUAL_HEIGHT + 100; // extend below world bottom

  const pulseT = state.riseIntensity > 0
    ? Math.sin(state.edgeTime * VOID.GRADIENT_PULSE_SPEED) * VOID.GRADIENT_PULSE_AMP * state.riseIntensity
    : 0;

  for (let y = startY; y < endY; y += stripHeight) {
    const t = Math.min(1, (y - startY) / topFade); // 0 at top, 1 at edge
    const deepT = Math.min(1, (y - voidY) / 200);  // transition to darkest below edge

    // Read void colors from active theme
    const theme = getVoidTheme();
    const deepR = (theme.voidDeepColor >> 16) & 0xff;
    const deepG = (theme.voidDeepColor >> 8) & 0xff;
    const deepB = theme.voidDeepColor & 0xff;
    const darkR = (theme.voidDarkestColor >> 16) & 0xff;
    const darkG = (theme.voidDarkestColor >> 8) & 0xff;
    const darkB = theme.voidDarkestColor & 0xff;

    let r: number, g: number, b: number, a: number;
    if (y < voidY) {
      // Above edge: fade in
      a = t * 0.6 + pulseT;
      r = deepR / 255;
      g = deepG / 255;
      b = deepB / 255;
    } else {
      // Below edge: deep → darkest
      a = 0.85 + deepT * 0.15 + pulseT;
      r = lerp(deepR, darkR, deepT) / 255;
      g = lerp(deepG, darkG, deepT) / 255;
      b = lerp(deepB, darkB, deepT) / 255;
    }

    // Scanline texture: every 3rd strip slightly brighter during rise
    const stripIndex = Math.floor((y - startY) / stripHeight);
    if (state.riseIntensity > 0 && stripIndex % 3 === 0) {
      a += 0.03 * state.riseIntensity;
    }

    a = Math.max(0, Math.min(1, a));

    const hex = (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255);
    gfx.rect(v.off, y, v.w, stripHeight);
    gfx.fill({ color: hex, alpha: a });
  }
}
